// CommFirmaWahl.tsx — Unternehmen eines geschäftlichen Kommunikationswegs,
// direkt im Feld-Label (User 30.09.2026, Mehrfachauswahl seit 02.10.2026).
//
// Die Firmen stehen nur einmal da: links grau im Label, als dezenter Text
// ohne Knopf-Optik. Ein Klick öffnet ein kleines Menü OHNE Kästchen — eine
// gewählte Zeile ist hervorgehoben und trägt ein ✓; ein Klick schaltet sie
// um, „Fertig" schließt. „(Keine Firma)" gibt es nicht mehr.
//
//   - Firmen wirksam (gewählt oder die einzige): „A", „A und B", „A, B, +x",
//     zu lang „A, weitere" bzw. „A …" — voller Text im Tooltip
//   - keine Firma, ≥ 2 Firmen wählbar: „Unternehmen wählen"
//   - keine Firma, ≤ 1 wählbar: nichts
//
// Genau EINE wählbare Firma ist immer gesetzt — ihr Eintrag ist gesperrt. Ist
// sonst nichts umzuschalten, öffnet der Text kein Menü.
//
// Kein <button>: Ein gesperrtes Formular (`<fieldset disabled>`) schaltet
// Knöpfe tot. Das Element bleibt sichtbar, öffnet dort aber nichts. Es sitzt
// im <label> des Felds — der Klick darf nicht ins Eingabefeld springen.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { imGesperrtenFormular } from "../AnsichtsKnopf";
import { firmenAnzeige } from "../../../utils/firmenAnzeige";
import { gleicheFirma } from "../../../utils/geschaeftlichFirma";
import { firmaUmschalten, firmenOptionen, gesperrteFirma } from "./UnternehmenMehrfachWahl";
import { alleDokumente, alleFenster, aktivesDokument, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

export interface CommFirmaOption {
  id?: number;
  name: string;
}

export interface CommFirmaWahlProps {
  /** Wählbare Firmen (nur aktive: Arbeitgeber + nicht ausgeschiedene Verbindungen). */
  firmen: CommFirmaOption[];
  /** Wirksame Firmen (gewählt bzw. die einzige), leer = keine. */
  gewaehlt: CommFirmaOption[];
  onChange: (gewaehlt: CommFirmaOption[]) => void;
}

/** Was steht im Label? `null` = gar nichts anzeigen. `voll` gehört in den Tooltip. */
export function commFirmaText(
  gewaehlt: readonly CommFirmaOption[],
  firmen: readonly CommFirmaOption[],
): { text: string; voll: string } | null {
  const a = firmenAnzeige(gewaehlt.map((f) => f.name));
  if (a.text) return a;
  return firmen.length >= 2 ? { text: "Unternehmen wählen", voll: "" } : null;
}

const useStyles = makeStyles({
  text: {
    color: "#736871",
    fontSize: "0.75rem",
    fontWeight: 400,
    cursor: "pointer",
    ...shorthands.borderRadius("4px"),
    ":hover": { textDecorationLine: "underline" },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: REFINED.aub700,
      outlineOffset: "1px",
    },
  },
  gesperrt: {
    cursor: "default",
    ":hover": { textDecorationLine: "none" },
  },
  menu: {
    position: "fixed",
    zIndex: 100010,
    minWidth: "180px",
    maxWidth: "320px",
    backgroundColor: "#fff",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 8px 24px rgba(58,12,49,.16), 0 2px 6px rgba(58,12,49,.08)",
    ...shorthands.padding("4px"),
    display: "flex",
    flexDirection: "column",
  },
  item: {
    textAlign: "left",
    backgroundColor: "transparent",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("7px", "10px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.85rem",
    color: REFINED.ink,
    cursor: "pointer",
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
    ":hover": { backgroundColor: "rgba(125, 32, 105, 0.07)" },
    ":focus-visible": { backgroundColor: "rgba(125, 32, 105, 0.1)", outlineStyle: "none" },
  },
  itemAktiv: {
    fontWeight: 600,
    color: "#7d2069",
    backgroundColor: "rgba(125, 32, 105, 0.1)",
    ":hover": { backgroundColor: "rgba(125, 32, 105, 0.14)" },
  },
  itemGesperrt: { cursor: "default" },
  itemZeile: { display: "flex", alignItems: "center", columnGap: "8px" },
  haken: { width: "14px", flexShrink: 0, textAlign: "center" },
  itemName: { whiteSpace: "nowrap", ...shorthands.overflow("hidden"), textOverflow: "ellipsis" },
  fuss: {
    display: "flex",
    justifyContent: "flex-end",
    ...shorthands.padding("6px", "4px", "2px"),
    borderTop: `1px solid ${REFINED.line}`,
    marginTop: "4px",
  },
  fertig: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.aub,
    color: "#fff",
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "4px 12px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub700 },
  },
});

export function CommFirmaWahl({ firmen, gewaehlt, onChange }: CommFirmaWahlProps) {
  const s = useStyles();
  const ankerRef = React.useRef<HTMLSpanElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [offen, setOffen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const [gesperrt, setGesperrt] = React.useState(false);

  // Gesperrt oder nicht, sagt erst das DOM (das fieldset liegt beim Aufrufer).
  React.useLayoutEffect(() => {
    const g = imGesperrtenFormular(ankerRef.current);
    if (g !== gesperrt) setGesperrt(g);
    if (g && offen) setOffen(false);
  });

  const schliessen = React.useCallback((fokusZurueck = false) => {
    setOffen(false);
    if (fokusZurueck) ankerRef.current?.focus();
  }, []);

  React.useLayoutEffect(() => {
    if (!offen) {
      setPos(null);
      return;
    }
    const r = ankerRef.current?.getBoundingClientRect();
    if (!r) return;
    const breite = menuRef.current?.offsetWidth ?? 200;
    const hoehe = menuRef.current?.offsetHeight ?? 0;
    const left = Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - breite - 8));
    const unten = r.bottom + 4;
    const top = hoehe && unten + hoehe > aktivesFenster().innerHeight - 8 ? Math.max(8, r.top - hoehe - 4) : unten;
    setPos({ top, left });
  }, [offen]);

  React.useEffect(() => {
    if (!offen) return;
    const weg = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || ankerRef.current?.contains(t)) return;
      schliessen();
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        schliessen(true);
      }
    };
    const scroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      schliessen();
    };
    alleDokumente.addEventListener("mousedown", weg, true);
    alleDokumente.addEventListener("keydown", taste, true);
    alleFenster.addEventListener("scroll", scroll, true);
    alleFenster.addEventListener("resize", scroll);
    // Fokus auf den ersten gewählten (oder ersten) Eintrag.
    const aktiv = menuRef.current?.querySelector<HTMLElement>('[aria-checked="true"]:not([aria-disabled="true"])') ??
      menuRef.current?.querySelector<HTMLElement>('[role="menuitemcheckbox"]');
    aktiv?.focus();
    return () => {
      alleDokumente.removeEventListener("mousedown", weg, true);
      alleDokumente.removeEventListener("keydown", taste, true);
      alleFenster.removeEventListener("scroll", scroll, true);
      alleFenster.removeEventListener("resize", scroll);
    };
  }, [offen, schliessen]);

  const anzeige = commFirmaText(gewaehlt, firmen);
  if (!anzeige) return null;

  const fest = gesperrteFirma(firmen);
  const optionen = firmenOptionen(firmen, gewaehlt);
  // Gibt es überhaupt etwas umzuschalten? Bei genau einer (gesperrten) Firma nicht.
  const umschaltbar = optionen.some((f) => !fest || !gleicheFirma(fest, f));
  const leer = gewaehlt.length === 0;

  const umschalten = (el: HTMLElement) => {
    if (imGesperrtenFormular(el) || !umschaltbar) return;
    setOffen((o) => !o);
  };

  const pfeilNavigation = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitemcheckbox"], button[data-fertig]') ?? []);
    const i = items.indexOf(aktivesDokument().activeElement as HTMLElement);
    const n = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[n]?.focus();
  };

  const beschriftung = leer ? "Unternehmen wählen" : `Unternehmen: ${anzeige.voll}`;

  return (
    <>
      <span
        ref={ankerRef}
        role={umschaltbar ? "button" : undefined}
        tabIndex={umschaltbar ? 0 : undefined}
        aria-haspopup={umschaltbar ? "menu" : undefined}
        aria-expanded={umschaltbar ? offen : undefined}
        aria-disabled={(umschaltbar && gesperrt) || undefined}
        aria-label={umschaltbar ? beschriftung : undefined}
        // Voller Text immer im Tooltip (der sichtbare ist ggf. gekürzt).
        title={
          anzeige.voll ||
          (umschaltbar && !gesperrt ? "Diesen geschäftlichen Kommunikationsweg Unternehmen zuordnen" : undefined)
        }
        className={mergeClasses(s.text, (gesperrt || !umschaltbar) && s.gesperrt)}
        onMouseDown={(e) => {
          // Kein Fokus-Sprung ins Eingabefeld über das <label>.
          e.preventDefault();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          umschalten(e.currentTarget);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || (e.key === "ArrowDown" && !offen)) {
            e.preventDefault();
            e.stopPropagation();
            umschalten(e.currentTarget);
          }
        }}
      >
        {anzeige.text}
      </span>
      {offen &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Unternehmen zuordnen"
            className={s.menu}
            style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
            onKeyDown={pfeilNavigation}
          >
            {optionen.map((f) => {
              const istFest = !!fest && gleicheFirma(fest, f);
              const aktiv = istFest || gewaehlt.some((g) => gleicheFirma(g, f));
              return (
                <button
                  key={`f-${f.id ?? ""}-${f.name}`}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={aktiv}
                  aria-disabled={istFest || undefined}
                  className={mergeClasses(s.item, aktiv && s.itemAktiv, istFest && s.itemGesperrt)}
                  title={istFest ? `${f.name} — die einzige Firma, immer zugeordnet` : f.name}
                  onClick={() => {
                    if (istFest) return;
                    onChange(firmaUmschalten(gewaehlt, f));
                  }}
                >
                  <span className={s.itemZeile}>
                    <span className={s.haken} aria-hidden="true">
                      {aktiv ? "✓" : ""}
                    </span>
                    <span className={s.itemName}>{f.name}</span>
                  </span>
                </button>
              );
            })}
            <div className={s.fuss}>
              <button type="button" data-fertig className={s.fertig} onClick={() => schliessen(true)}>
                Fertig
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default CommFirmaWahl;
